import React, { useState, useEffect } from 'react';
import { Handle, Position } from '@xyflow/react';
import { Popconfirm, Card, Input, InputNumber, Button, Space, Checkbox, Typography, Tabs, Tag, Tooltip } from 'antd';
import { 
  DeleteOutlined, 
  PlusOutlined, 
  InfoCircleOutlined, 
  SettingOutlined,
  FileTextOutlined,
  BarChartOutlined,
  SearchOutlined,
  EditOutlined,
  LineChartOutlined
} from '@ant-design/icons';
import { Icon } from '@iconify/react';
import NodeInputHandler from '../common/NodeInputHandler';
import GenericNodeContent from '../common/GenericNodeContent';

const { TextArea } = Input;
const { Text } = Typography;
const { TabPane } = Tabs;

const createSpecializedAINode = (title, description, icon, extraInputs = []) => {
  return ({ id, data, isConnectable }) => {
    const [nodeName, setNodeName] = useState(data.nodeName || `${title} ${id}`);
    const [activeTab, setActiveTab] = useState('input');

    const handleNameChange = (e) => {
      const newName = e.target.value;
      setNodeName(newName);
      data.updateNodeData(id, { nodeName: newName });
    };

    const handleContentChange = (nodeId, newData) => {
      data.updateNodeData(nodeId, newData);
    };

    const handleInputChange = (key, value) => {
      data.updateNodeData(id, { [key]: value });
    };


    

    if (title === 'Optimize for SEO') {
      const [keywords, setKeywords] = useState(data.keywords || '');

      useEffect(() => {
        data.updateNodeData(id, { nodeName, keywords });
      }, [id, nodeName, keywords, data]);

      return (
        <Card 
          title={
            <Space>
              <LineChartOutlined style={{ fontSize: '18px', color: '#1890ff' }}/>
              <span>{title}</span>
              <Tag color="blue">SEO</Tag>
            </Space>
          }
          style={{ width: 380 }}
          extra={
            <Space>
              <Button 
                icon={<SettingOutlined />}
                type="text"
                onClick={() => setActiveTab('settings')}
              />
      <Popconfirm
        title="Delete Node"
        description="Are you sure you want to delete this node?"
        onConfirm={() => data.onDelete(id)}
        okText="Delete"
        cancelText="Cancel"
        okButtonProps={{ danger: true }} > 
        <Button icon={<DeleteOutlined />} /> </Popconfirm>
            </Space>
          }
        >
          <Handle type="target" position={Position.Left} isConnectable={isConnectable} />
          
          <Input
            placeholder="Node Name"
            value={nodeName}
            onChange={handleNameChange}
            style={{ marginBottom: 16 }}
            prefix={<InfoCircleOutlined style={{ color: '#bfbfbf' }} />}
            className="nodrag"
          />

          <Tabs activeKey={activeTab} onChange={setActiveTab}>
            <TabPane tab="Content" key="input">
              <Space direction="vertical" style={{ width: '100%' }}>
                <div>
                  <Space align="center" style={{ marginBottom: 4 }}>
                    <Text strong>Content to Optimize</Text>
                    <Tooltip title="Enter the text you want to optimize for SEO">
                      <InfoCircleOutlined style={{ color: '#bfbfbf' }} />
                    </Tooltip>
                  </Space>
                  <GenericNodeContent
                    id={id}
                    data={data}
                    onChange={handleContentChange}
                    placeholder="Enter text to optimize for SEO..."
                  />
                </div>

                <div>
                  <Space align="center" style={{ marginBottom: 4 }}>
                    <Text strong>Target Keywords</Text>
                    <Tooltip title="Enter comma-separated keywords to target">
                      <InfoCircleOutlined style={{ color: '#bfbfbf' }} />
                    </Tooltip>
                  </Space>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                    <Input
                      placeholder="e.g., seo, optimization, keywords"
                      value={keywords}
                      onChange={(e) => {
                        setKeywords(e.target.value);
                        handleInputChange('keywords', e.target.value);
                      }}
                      className="nodrag"
                      style={{ width: '100%' }}
                    />
                    <div>
                      <NodeInputHandler 
                        nodeId={id} 
                        onInsert={(tag) => {
                          const newKeywords = keywords ? `${keywords}, ${tag}` : tag;
                          setKeywords(newKeywords);
                          handleInputChange('keywords', newKeywords);
                        }}
                      />
                    </div>
                  </div>
                </div>
              </Space>
            </TabPane>

            <TabPane tab="Output" key="output">
              <Space direction="vertical" style={{ width: '100%' }}>
                <Text type="secondary">
                  Available outputs from this node:
                </Text>
                <div style={{ 
                  backgroundColor: '#f5f5f5', 
                  padding: '12px', 
                  borderRadius: '6px'
                }}>
                  <Space direction="vertical">
                    <div>
                      <Text strong>Optimized Content</Text>
                      <div>
                        <Text code>[Input from {id}]</Text>
                      </div>
                    </div>
                  </Space>
                </div>
              </Space>
            </TabPane>
          </Tabs>

          <Handle type="source" position={Position.Right} id="a" isConnectable={isConnectable} />
        </Card>
      );
    }

    

    return (
      <Card 
        title={
          <Space>
            {icon}
            <span>{title}</span>
            <Tag color="blue">AI</Tag>
          </Space>
        }
        style={{ width: 380 }}
        extra={
          <Space>
            <Button 
              icon={<SettingOutlined />}
              type="text"
              onClick={() => setActiveTab('settings')}
            />
      <Popconfirm
        title="Delete Node"
        description="Are you sure you want to delete this node?"
        onConfirm={() => data.onDelete(id)}
        okText="Delete"
        cancelText="Cancel"
        okButtonProps={{ danger: true }} > 
        <Button icon={<DeleteOutlined />} /> </Popconfirm>
          </Space>
        }
      >
        <Handle type="target" position={Position.Left} isConnectable={isConnectable} />
        
        <Input
          placeholder="Node Name"
          value={nodeName}
          onChange={handleNameChange}
          style={{ marginBottom: 16 }}
          prefix={<InfoCircleOutlined style={{ color: '#bfbfbf' }} />}
          className="nodrag"
        />

        <Tabs activeKey={activeTab} onChange={setActiveTab}>
          <TabPane tab="Input" key="input">
            <Space direction="vertical" style={{ width: '100%' }}>
              <div>
                <Space align="center" style={{ marginBottom: 4 }}>
                  <Text strong>Content</Text>
                  <Tooltip title={description}>
                    <InfoCircleOutlined style={{ color: '#bfbfbf' }} />
                  </Tooltip>
                </Space>
                <GenericNodeContent
                  id={id}
                  data={data}
                  onChange={handleContentChange}
                  placeholder="Enter text to analyze..."
                />
              </div>

              {extraInputs.map(input => (
                <div key={input.key}>
                  <Space align="center" style={{ marginBottom: 4 }}>
                    <Text strong>{input.label}</Text>
                    {input.description && (
                      <Tooltip title={input.description}>
                        <InfoCircleOutlined style={{ color: '#bfbfbf' }} />
                      </Tooltip>
                    )}
                  </Space>
                  <div style={{ 
                    padding: 8,
                    background: '#f5f5f5',
                    borderRadius: 6
                  }}>
                    {input.type === 'number' ? (
                      <InputNumber
                        style={{ width: '100%' }}
                        value={data[input.key]}
                        onChange={(value) => handleInputChange(input.key, value)}
                        className="nodrag"
                        min={input.min}
                        max={input.max}
                      />
                    ) : (
                      <Input
                        value={data[input.key]}
                        onChange={(e) => handleInputChange(input.key, e.target.value)}
                        className="nodrag"
                      />
                    )}
                  </div>
                </div>
              ))}
            </Space>
          </TabPane>

          <TabPane tab="Output" key="output">
            <Space direction="vertical" style={{ width: '100%' }}>
              <Text type="secondary">
                Available outputs from this node:
              </Text>
              <div style={{ 
                backgroundColor: '#f5f5f5', 
                padding: '12px', 
                borderRadius: '6px'
              }}>
                <Space direction="vertical">
                  <div>
                    <Text strong>Analysis Results</Text>
                    <div>
                      <Text code>[Input from {id}]</Text>
                    </div>
                  </div>
                </Space>
              </div>
            </Space>
          </TabPane>
        </Tabs>

        <Handle type="source" position={Position.Right} id="a" isConnectable={isConnectable} />
      </Card>
    );
  };
};

// Node definitions with icons
export const SentimentAnalysisNode = createSpecializedAINode(
  'Sentiment Analysis',
  'Analyzes and returns the sentiment of the provided text (very negative, negative, neutral, positive, very positive).',
  <BarChartOutlined style={{ fontSize: '18px', color: '#1890ff' }}/>
);

export const OptimizeSEONode = createSpecializedAINode(
  'Optimize for SEO',
  'Optimizes text for SEO based on provided keywords.',
  <LineChartOutlined style={{ fontSize: '18px', color: '#1890ff' }}/>,
  [
    { key: 'keywords', label: 'SEO Keywords (comma-separated)' }
  ]
);

export const SummaryGeneratorNode = createSpecializedAINode(
  'Summary Generator',
  'Summarizes text into key points.',
  <FileTextOutlined style={{ fontSize: '18px', color: '#1890ff' }}/>,
  [
    { 
      key: 'maxLength',
      label: 'Maximum Length',
      type: 'number',
      min: 50,
      max: 500,
      description: 'Maximum word count for the summary'
    }
  ]
);

export const WriteArticleNode = createSpecializedAINode(
  'Write Article',
  'Generates an article based on the provided input.',
  <EditOutlined style={{ fontSize: '18px', color: '#1890ff' }}/>,
  [
    { 
      key: 'wordCount',
      label: 'Word Count',
      type: 'number',
      min: 100,
      max: 5000,
      description: 'Target word count for the article'
    },
    {
      key: 'style',
      label: 'Writing Style',
      type: 'text',
      description: 'Describe the desired writing style'
    }
  ]
);

// Information Extraction Node gets its own implementation
export const ExtractInformationNode = ({ id, data, isConnectable }) => {
  const [nodeName, setNodeName] = useState(data.nodeName || `Extract Information ${id}`);
  const [activeTab, setActiveTab] = useState('input');
  const [extractionFields, setExtractionFields] = useState(data.extractionFields || []);

  useEffect(() => {
    data.updateNodeData(id, { 
      nodeName,
      extractionFields
    });
  }, [id, nodeName, extractionFields, data]);

  const handleContentChange = (nodeId, newData) => {
    data.updateNodeData(nodeId, newData);
  };

  const handleFieldChange = (index, fieldName, value) => {
    const newFields = [...extractionFields];
    newFields[index] = { 
      ...newFields[index], 
      [fieldName]: value 
    };
    setExtractionFields(newFields);
  };

  const addExtractionField = () => {
    setExtractionFields([
      ...extractionFields,
      { name: '', description: '', isList: false }
    ]);
  };

  const removeExtractionField = (index) => {
    setExtractionFields(fields => 
      fields.filter((_, i) => i !== index)
    );
  };

  return (
    <Card 
      title={
        <Space>
          <SearchOutlined style={{ fontSize: '18px', color: '#1890ff' }}/>
          <span>Extract Information</span>
          <Tag color="blue">AI Extract</Tag>
        </Space>
      }
      style={{ width: 380 }}
      extra={
        <Space>
          <Button 
            icon={<SettingOutlined />}
            type="text"
            onClick={() => setActiveTab('settings')}
          />
      <Popconfirm
        title="Delete Node"
        description="Are you sure you want to delete this node?"
        onConfirm={() => data.onDelete(id)}
        okText="Delete"
        cancelText="Cancel"
        okButtonProps={{ danger: true }} > 
        <Button icon={<DeleteOutlined />} /> </Popconfirm>
        </Space>
      }
    >
      <Handle type="target" position={Position.Left} isConnectable={isConnectable} />
      
      <Input
        placeholder="Node Name"
        value={nodeName}
        onChange={(e) => setNodeName(e.target.value)}
        style={{ marginBottom: 16 }}
        prefix={<InfoCircleOutlined style={{ color: '#bfbfbf' }} />}
        className="nodrag"
      />

      <Tabs activeKey={activeTab} onChange={setActiveTab}>
        <TabPane tab="Content" key="input">
          <Space direction="vertical" style={{ width: '100%' }}>
            <div>
              <Space align="center" style={{ marginBottom: 4 }}>
                <Text strong>Text to Analyze</Text>
                <Tooltip title="Enter the text from which you want to extract information">
                  <InfoCircleOutlined style={{ color: '#bfbfbf' }} />
                </Tooltip>
              </Space>
              <GenericNodeContent
                id={id}
                data={data}
                onChange={handleContentChange}
                placeholder="Enter text to analyze..."
              />
            </div>

            <div style={{ marginTop: 16 }}>
              <Space align="center" style={{ marginBottom: 8 }}>
                <Text strong>Extraction Fields</Text>
                <Tooltip title="Define what information to extract">
                  <InfoCircleOutlined style={{ color: '#bfbfbf' }} />
                </Tooltip>
              </Space>

              {extractionFields.map((field, index) => (
                <div 
                  key={index}
                  style={{ 
                    padding: 12,
                    background: '#f5f5f5',
                    borderRadius: 6,
                    marginBottom: 8
                  }}
                >
                  <Space direction="vertical" style={{ width: '100%' }}>
                    <Input
                      placeholder="Field Name (e.g., 'author', 'date', 'price')"
                      value={field.name}
                      onChange={(e) => handleFieldChange(index, 'name', e.target.value)}
                      className="nodrag"
                    />
                    <Input
                      placeholder="Field Description"
                      value={field.description}
                      onChange={(e) => handleFieldChange(index, 'description', e.target.value)}
                      className="nodrag"
                    />
                    <Space>
                      <Checkbox
                        checked={field.isList}
                        onChange={(e) => handleFieldChange(index, 'isList', e.target.checked)}
                      >
                        Return as List
                      </Checkbox>
                      <Tooltip title="Use this for extracting multiple items like product features">
                        <InfoCircleOutlined style={{ color: '#bfbfbf' }} />
                      </Tooltip>
                      <Popconfirm
        title="Delete Node"
        description="Are you sure you want to delete this node?"
        onConfirm={() => data.onDelete(id)}
        okText="Delete"
        cancelText="Cancel"
        okButtonProps={{ danger: true }} > 
        <Button icon={<DeleteOutlined />} /> </Popconfirm>
                    </Space>
                  </Space>
                </div>
              ))}

              <Button 
                type="dashed"
                icon={<PlusOutlined />}
                onClick={addExtractionField}
                style={{ width: '100%' }}
              >
                Add Extraction Field
              </Button>
            </div>
          </Space>
        </TabPane>

        <TabPane tab="Output" key="output">
          <Space direction="vertical" style={{ width: '100%' }}>
            <Text type="secondary">
              Available outputs from this node:
            </Text>
            <div style={{ 
              backgroundColor: '#f5f5f5', 
              padding: '12px', 
              borderRadius: '6px'
            }}>
              <Space direction="vertical">
                {extractionFields.map((field, index) => (
                  <div key={index}>
                    <Text strong>{field.name || `Field ${index + 1}`}</Text>
                    <div>
                      <Text code>{`[[${field.name || `field${index + 1}`}] from ${id}]`}</Text>
                    </div>
                  </div>
                ))}
                <div>
                  <Text strong>All Extracted Data</Text>
                  <div>
                    <Text code>{`[[extractedData] from ${id}]`}</Text>
                  </div>
                </div>
              </Space>
            </div>
          </Space>
        </TabPane>

        <TabPane tab="Examples" key="examples">
          <Space direction="vertical" style={{ width: '100%' }}>
            <Text type="secondary">
              Example configurations for common use cases:
            </Text>
            
            <div style={{ 
              backgroundColor: '#f5f5f5', 
              padding: '12px', 
              borderRadius: '6px'
            }}>
              <Space direction="vertical">
                <div>
                  <Text strong>Product Information</Text>
                  <ul style={{ margin: '4px 0 8px 20px' }}>
                    <li>name (single value)</li>
                    <li>price (single value)</li>
                    <li>features (list)</li>
                    <li>specifications (list)</li>
                  </ul>
                </div>

                <div>
                  <Text strong>Article Metadata</Text>
                  <ul style={{ margin: '4px 0 8px 20px' }}>
                    <li>author (single value)</li>
                    <li>date (single value)</li>
                    <li>categories (list)</li>
                    <li>references (list)</li>
                  </ul>
                </div>

                <Button 
                  type="link" 
                  onClick={() => {
                    setExtractionFields([
                      { name: 'name', description: 'Extract the product name', isList: false },
                      { name: 'price', description: 'Extract the price value', isList: false },
                      { name: 'features', description: 'Extract all product features', isList: true },
                      { name: 'specifications', description: 'Extract technical specifications', isList: true }
                    ]);
                    setActiveTab('input');
                  }}
                >
                  Use Product Template
                </Button>
                <Button 
                  type="link"
                  onClick={() => {
                    setExtractionFields([
                      { name: 'author', description: 'Extract the author name', isList: false },
                      { name: 'date', description: 'Extract the publication date', isList: false },
                      { name: 'categories', description: 'Extract article categories', isList: true },
                      { name: 'references', description: 'Extract reference links or citations', isList: true }
                    ]);
                    setActiveTab('input');
                  }}
                >
                  Use Article Template
                </Button>
              </Space>
            </div>
          </Space>
        </TabPane>
      </Tabs>

      <Handle type="source" position={Position.Right} id="a" isConnectable={isConnectable} />
    </Card>
  );
};

export default {
  SentimentAnalysisNode,
  SummaryGeneratorNode,
  WriteArticleNode,
  ExtractInformationNode,
  OptimizeSEONode,
};